import React, { useState, useEffect, useRef, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { colors } from '@omilia/theme';
import { MinusIcon, KebabIcon } from '@omilia/icon';
import { v4 as uuid } from 'uuid';

import Logo from 'components/Logo';
import ContextMenu from './components/ContextMenu/ContextMenu';

import { getThemeProp } from 'styles/theme';
import { getSessionStorage, setSessionStorage } from 'services/sessionStorage';
import { STORAGE_KEY } from 'constants/browserStorageKeys';
import { ReopenMinimizedChatState, ReopenMinimiziedChat } from 'types';
import { setStorageItem } from 'services/storage';
import { useTranslation } from '@omilia/providers';

interface Props {
  minimizeChatBox: (isMinimized: boolean) => void;
  closeChatBox: () => void;
  restartChat: () => void;
  isRestartEnabled: boolean;
  isMinimized: boolean;
  enableTranscriptDownload: boolean;
  subtitle?: string;
  shouldReopenMinimizedChat?: ReopenMinimiziedChat;
}

const SHeader = styled.div`
  align-items: center;
  background-color: ${getThemeProp('primaryColor')};
  border-radius: 24px 24px 0 0;
  box-shadow: 0 9px 18px 0 rgba(0, 0, 0, 0.08), 0 2px 4px 0 rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  display: flex;
  min-height: 59px;
  justify-content: space-between;
  padding: 0 16px 5px 16px;
  z-index: 1;
`;

const SInnerHeader = styled.div`
  align-items: center;
  display: inline-flex;
  width: 80%;
`;

const SLogo = styled(Logo)<{ bordered: boolean }>`
  border: ${({ bordered }) =>
    bordered ? `1px solid ${colors.COLOR_WHITE}` : 'none'};
  height: 33px;
  margin-right: 12px;
  width: 33px;
`;

const STitleContainer = styled.div<{ showShadow: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  width: 100%;

  &::after {
    box-shadow: 10px 0px 20px 30px ${getThemeProp('primaryColor')};
    content: '';
    display: block;
    height: 22px;
    opacity: ${({ showShadow }) => (showShadow ? 1 : 0)};
    position: absolute;
    right: 0;
    top: 0;
  }
`;

const STitle = styled.span<{ offset: number; animationTime: number }>`
  color: ${colors.COLOR_WHITE};
  font-family: ${getThemeProp('fontFamily')};
  font-size: 16px;
  font-weight: 600;
  left: 0;
  line-height: 25px;
  position: relative;
  transition: ${({ animationTime }) => `left ${animationTime}s linear`};
  white-space: nowrap;

  &:hover {
    left: ${({ offset }) => `-${offset}px`};
    transition: ${({ animationTime }) => `left ${animationTime}s linear`};
  }
`;

const SSubtitle = styled.span`
  color: ${colors.COLOR_WHITE};
  font-size: 12px;
  font-family: ${getThemeProp('fontFamily')};
`;

const SControls = styled.div`
  align-items: center;
  display: inline-flex;
  justify-content: flex-end;
  width: 20%;
`;

const SControl = styled.button`
  background: none;
  border: none;
  box-sizing: content-box;
  color: ${colors.COLOR_WHITE};
  cursor: pointer;
  display: inline-block;
  height: 24px;
  margin-left: 16px;
  padding: 0;
  text-align: center;
  width: 24px;
`;

const SMenuWrapper = styled.div`
  position: relative;
`;

const SMenuButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  display: flex;
  height: 34px;
  justify-content: center;
  margin-left: 8px;
  padding: 0;
  transition: background-color 0.15s ease;
  width: 34px;

  &:focus-visible,
  &:hover {
    background-color: color-mix(
      in srgb,
      ${getThemeProp('primaryColor')} 72%,
      black
    );
  }
`;

const FADE_BOX_WIDTH = 20;
const MAX_TITLE_LENGTH = 150;
const MAX_SUBTITLE_LENGTH = 21;

const ChatHeader = ({
  minimizeChatBox,
  closeChatBox,
  restartChat,
  isRestartEnabled,
  enableTranscriptDownload,
  subtitle,
  shouldReopenMinimizedChat,
}: Props) => {
  const scrollRef = useRef(null);
  const titleRef = useRef(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const theme = useContext(ThemeContext);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [titleWidth, setTitleWidth] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuMounted, setMenuMounted] = useState(false);

  const logoUrl = getThemeProp('logoUrl', { theme });
  const logoAlt = getThemeProp('logoAlt', { theme });
  const logoBorder = getThemeProp('logoBorder', { theme });
  const title = getThemeProp('title', { theme });

  const { t } = useTranslation();

  useEffect(() => {
    if (!scrollRef.current || !titleRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setWidthValues();
    });
    resizeObserver.observe(scrollRef.current);
    resizeObserver.observe(titleRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    setWidthValues();
  }, [title, subtitle]);

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const root = (menuRef.current?.getRootNode() ?? document) as
      | Document
      | ShadowRoot;
    root.addEventListener('mousedown', handleClickOutside);
    return () => root.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const setWidthValues = () => {
    setScrollWidth(scrollRef?.current?.scrollWidth);
    setTitleWidth(titleRef?.current?.getBoundingClientRect().width);
  };

  const trim = (string: string, maxLength: number) => {
    return string.substring(0, maxLength);
  };

  const getOffset = () => {
    return scrollWidth <= Math.round(titleWidth)
      ? 0
      : scrollWidth - (titleWidth - FADE_BOX_WIDTH);
  };

  const handleMinimize = () => {
    if (
      !getSessionStorage(STORAGE_KEY.TAB_SESSION_ID) &&
      shouldReopenMinimizedChat === ReopenMinimizedChatState.NEXT_TABS
    ) {
      const tabSessionId = uuid();
      setSessionStorage(STORAGE_KEY.TAB_SESSION_ID, tabSessionId);
      setStorageItem(STORAGE_KEY.TAB_SESSION_ID, tabSessionId);
      setStorageItem(STORAGE_KEY.MINIMIZED_CHAT, false);
    }

    minimizeChatBox(true);
    setStorageItem(STORAGE_KEY.MINIMIZED_CHAT, true);
  };

  return (
    <SHeader data-test="chat-header">
      <SInnerHeader>
        <SLogo src={logoUrl} alt={logoAlt || ''} bordered={logoBorder} />
        <STitleContainer ref={titleRef} showShadow={getOffset() > 0}>
          <STitle
            ref={scrollRef}
            offset={getOffset()}
            animationTime={getOffset() / 100}
          >
            {trim(title, MAX_TITLE_LENGTH)}
          </STitle>
          {subtitle && (
            <SSubtitle>{trim(subtitle, MAX_SUBTITLE_LENGTH)}</SSubtitle>
          )}
        </STitleContainer>
      </SInnerHeader>

      <SControls>
        <SControl
          onClick={handleMinimize}
          data-test="icon-minimize"
          aria-label={t('header.minimize')}
        >
          <MinusIcon
            size="14"
            data-test="button-minimize-chat"
            color={colors.COLOR_WHITE}
          />
        </SControl>
        <SMenuWrapper ref={menuRef}>
          <SMenuButton
            onClick={() => {
              setMenuMounted(true);
              setMenuOpen(prev => !prev);
            }}
            data-test="icon-menu"
            aria-label={t('header.openMenu')}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <KebabIcon size="20" color={colors.COLOR_WHITE} />
          </SMenuButton>
          {menuMounted && (
            <ContextMenu
              isOpen={menuOpen}
              isRestartEnabled={isRestartEnabled}
              enableTranscriptDownload={enableTranscriptDownload}
              onRestartChat={restartChat}
              onEndChat={closeChatBox}
              onClose={() => setMenuOpen(false)}
            />
          )}
        </SMenuWrapper>
      </SControls>
    </SHeader>
  );
};

export default ChatHeader;
